// The page <-> Helm XR glasses bridge (Erwin 2026-10-29 O1/O2/O3/S1; Nexus Docs/DESIGN_WEB_HANDOFF_STEPUP_2026-10-29.md).
//
// Helm XR's GeckoView extension (`hxwebauthn`, a top-frame content script) talks to this page over DOM CustomEvents on
// `document` — same document only. Every `detail` is a JSON string (the content script runs in an isolated world).
//
//   page -> nx-handoff:hello                 content script -> nx-handoff:ready {"v":1,"returnUri":"…"}
//   page -> nx-handoff:verifier-request      content script -> nx-handoff:verifier {"codeVerifier":"…"}   (S1)
//   page -> nx-handoff:stepup {userCode, verificationUri, expiresIn, admin, headset}   (opens the chooser)
//   content script -> nx-handoff:stepup-cancelled {}                                   (the chooser was closed)
//   page -> nx-handoff:stepup-done {}                                                  (the poll succeeded)
//
// We always listen BEFORE dispatching, accept only events whose target is this document itself (not ones bubbling up
// from an element), and ignore malformed details. Nothing here ever logs a detail (the verifier is a secret).

export interface BridgeTarget {
  addEventListener(type: string, fn: (e: Event) => void): void;
  removeEventListener(type: string, fn: (e: Event) => void): void;
  dispatchEvent(e: Event): boolean;
}

export const EV = {
  hello: 'nx-handoff:hello',
  ready: 'nx-handoff:ready',
  verifierRequest: 'nx-handoff:verifier-request',
  verifier: 'nx-handoff:verifier',
  stepup: 'nx-handoff:stepup',
  stepupCancelled: 'nx-handoff:stepup-cancelled',
  stepupDone: 'nx-handoff:stepup-done',
} as const;

export const VERIFIER_TIMEOUT_MS = 2500;
export const HELLO_TIMEOUT_MS = 1000;
/** RFC 7636 code_verifier: 43–128 unreserved characters. */
export const VERIFIER_RE = /^[A-Za-z0-9\-._~]{43,128}$/;

function defaultDoc(): BridgeTarget | null {
  return typeof document === 'undefined' ? null : document;
}

/** The detail of a bridge event as an object: a JSON string holding an object, else null. */
export function parseDetail(e: Event): Record<string, unknown> | null {
  const d = (e as CustomEvent<unknown>).detail;
  if (typeof d !== 'string') return null;
  try {
    const v: unknown = JSON.parse(d);
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function send(doc: BridgeTarget, type: string, detail?: unknown): void {
  try {
    doc.dispatchEvent(new CustomEvent(type, detail === undefined ? undefined : { detail: JSON.stringify(detail) }));
  } catch { /* no bridge */ }
}

/**
 * Dispatch `ask`, wait at most `ms` for an `answer` event on this document whose detail `accept` turns into a value;
 * events that are not on this document or that `accept` rejects (null) are ignored. Resolves null on timeout.
 */
function ask<T>(doc: BridgeTarget, askType: string, answerType: string, ms: number, accept: (d: Record<string, unknown>) => T | null): Promise<T | null> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (v: T | null) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      doc.removeEventListener(answerType, on);
      resolve(v);
    };
    const on = (e: Event) => {
      if (e.target !== doc) return;
      const d = parseDetail(e);
      const v = d ? accept(d) : null;
      if (v !== null) finish(v);
    };
    const timer = setTimeout(() => finish(null), ms);
    doc.addEventListener(answerType, on);   // listen first, then ask
    send(doc, askType);
  });
}

/** S1: the PKCE code verifier for the ticket in this URL, from the glasses (null when there is no bridge / no answer). */
export function requestVerifier(doc: BridgeTarget | null = defaultDoc(), ms = VERIFIER_TIMEOUT_MS): Promise<string | null> {
  if (!doc) return Promise.resolve(null);
  return ask(doc, EV.verifierRequest, EV.verifier, ms, (d) =>
    typeof d.codeVerifier === 'string' && VERIFIER_RE.test(d.codeVerifier) ? d.codeVerifier : null);
}

export interface BridgeHello { returnUri: string | null }

/** Is this page inside Helm XR? Resolves {returnUri} when the content script answers within 1 s, else null. */
export function hello(doc: BridgeTarget | null = defaultDoc(), ms = HELLO_TIMEOUT_MS): Promise<BridgeHello | null> {
  if (!doc) return Promise.resolve(null);
  return ask<BridgeHello>(doc, EV.hello, EV.ready, ms, (d) => ({ returnUri: typeof d.returnUri === 'string' && d.returnUri ? d.returnUri : null }));
}

export interface StepUpDetail {
  userCode: string;
  verificationUri: string;
  expiresIn: number;
  admin: boolean;
  headset: { eligible: boolean; reason?: string };
}

/** Open the step-up chooser in the glasses. */
export function requestStepUp(detail: StepUpDetail, doc: BridgeTarget | null = defaultDoc()): void {
  if (doc) send(doc, EV.stepup, detail);
}

/** Call `fn` when the person closes the chooser in the glasses. Returns the unsubscribe. */
export function onStepUpCancelled(fn: () => void, doc: BridgeTarget | null = defaultDoc()): () => void {
  if (!doc) return () => undefined;
  const on = (e: Event) => { if (e.target === doc) fn(); };
  doc.addEventListener(EV.stepupCancelled, on);
  return () => doc.removeEventListener(EV.stepupCancelled, on);
}

/** The poll succeeded: the glasses close the chooser. */
export function stepUpDone(doc: BridgeTarget | null = defaultDoc()): void {
  if (doc) send(doc, EV.stepupDone, {});
}
